import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Upload, ArrowLeft, Save, X } from "lucide-react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { MultiSelect } from "../components/ui/multi-select";
import { COUNTRIES, getStatesForCountry, getStateLabel } from "../lib/locationData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const goals = ["Muscle Building", "Weight Loss", "Weight Gain", "General Fitness", "Other"];
const relations = ["Father", "Mother", "Spouse", "Brother", "Sister", "Friend", "Other"];

const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));
const MONTHS = [
  { v: "01", l: "January" }, { v: "02", l: "February" }, { v: "03", l: "March" },
  { v: "04", l: "April" }, { v: "05", l: "May" }, { v: "06", l: "June" },
  { v: "07", l: "July" }, { v: "08", l: "August" }, { v: "09", l: "September" },
  { v: "10", l: "October" }, { v: "11", l: "November" }, { v: "12", l: "December" },
];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1919 }, (_, i) => String(currentYear - i));

const toNumOrNull = (v) =>
  (v === "" || v === null || v === undefined) ? null : Number(v);

// Static Options maps to prevent re-evaluation
const DAY_OPTIONS = DAYS.map((d) => ({ value: d, label: d }));
const MONTH_OPTIONS = MONTHS.map((m) => ({ value: m.v, label: m.l }));
const YEAR_OPTIONS = YEARS.map((y) => ({ value: y, label: y }));
const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c, label: c }));
const RELATION_OPTIONS = relations.map((r) => ({ value: r, label: r }));

// Memoized inputs and select dropdowns to prevent unnecessary renders while typing
const GenderSelect = memo(({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm mt-1" data-testid="member-gender"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="Male">Male</SelectItem>
      <SelectItem value="Female">Female</SelectItem>
      <SelectItem value="Other">Other</SelectItem>
    </SelectContent>
  </Select>
));
GenderSelect.displayName = "GenderSelect";

const DobSelect = memo(({ value, onChange, placeholder, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {options.map((opt) => (
        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
));
DobSelect.displayName = "DobSelect";

const StatusSelect = memo(({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="Active">Active</SelectItem>
      <SelectItem value="Inactive">Inactive</SelectItem>
    </SelectContent>
  </Select>
));
StatusSelect.displayName = "StatusSelect";

const CountrySelect = memo(({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm mt-1" data-testid="member-country"><SelectValue /></SelectTrigger>
    <SelectContent>
      {COUNTRY_OPTIONS.map((c) => (
        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
));
CountrySelect.displayName = "CountrySelect";

const StateSelect = memo(({ value, onChange, country }) => {
  const states = getStatesForCountry(country);
  const label = getStateLabel(country);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-sm mt-1" data-testid="member-state"><SelectValue placeholder={`Select ${label}`} /></SelectTrigger>
      <SelectContent>
        {states.map((s) => (
          <SelectItem key={s} value={s}>{s}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});
StateSelect.displayName = "StateSelect";

const RelationSelect = memo(({ value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
    <SelectContent>
      {RELATION_OPTIONS.map((r) => (
        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
));
RelationSelect.displayName = "RelationSelect";

const MemoizedPhoneInput = memo(({ value, onChange, ...props }) => (
  <PhoneInput
    value={value}
    onChange={onChange}
    {...props}
  />
));
MemoizedPhoneInput.displayName = "MemoizedPhoneInput";

const MemoizedMultiSelect = memo(({ options, selected, onChange, placeholder }) => (
  <MultiSelect
    options={options}
    selected={selected}
    onChange={onChange}
    placeholder={placeholder}
  />
));
MemoizedMultiSelect.displayName = "MemoizedMultiSelect";


// Parse stored "YYYY-MM-DD" into separate day/month/year parts
const parseDob = (dob) => {
  if (!dob) return { day: "", month: "", year: "" };
  const parts = dob.slice(0, 10).split("-");
  return { year: parts[0] || "", month: parts[1] || "", day: parts[2] || "" };
};

export default function MemberEdit() {
  const { id } = useParams();
  const { currentBranch, branches } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Separate DOB fields — same as MemberCreate
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");

  // New password (optional — only sent if filled)
  const [newPassword, setNewPassword] = useState("");

  const [form, setForm] = useState({
    name: "", phone: "", email: "", gender: "Male", address: "", state: "", city: "",
    pincode: "", country: "India", photo: "", emergencyContactName: "",
    emergencyContactRelation: "", emergencyContactPhone: "", medicalIssue: "",
    goal: "General Fitness", height: "", weight: "",
    professional: "", reference: "", joiningDate: "", notes: "", status: "Active",
    biometricId: "",
    branchId: "",
    branchIds: [],
  });

  useEffect(() => {
    const fetchMember = async () => {
      try {
        const res = await api.get(`/members/${id}`);
        const m = res.data.member;
        const dob = parseDob(m.dob);
        setDobDay(dob.day);
        setDobMonth(dob.month);
        setDobYear(dob.year);
        setForm({
          name: m.name || "",
          phone: m.phone || "",
          email: m.email || "",
          gender: m.gender || "Male",
          address: m.address || "",
          state: m.state || "",
          city: m.city || "",
          pincode: m.pincode || "",
          country: m.country || "India",
          photo: m.photo || "",
          emergencyContactName: m.emergencyContactName || "",
          emergencyContactRelation: m.emergencyContactRelation || "",
          emergencyContactPhone: m.emergencyContactPhone || "",
          medicalIssue: m.medicalIssue || "",
          goal: m.goal || "General Fitness",
          height: m.height ?? "",
          weight: m.weight ?? "",
          professional: m.professional || "",
          reference: m.reference || "",
          biometricId: m.biometricId || "",
          joiningDate: m.joiningDate ? m.joiningDate.slice(0, 10) : "",
          notes: m.notes || "",
          status: m.status || "Active",
          branchId: m.branchId || "",
          branchIds: m.branchIds || (m.branchId ? [m.branchId] : []),
        });
        if (m.photo) setPhotoPreview(`${BACKEND_URL}${m.photo}`);
      } catch { toast.error("Member not found"); navigate("/members"); }
      finally { setFetchLoading(false); }
    };
    fetchMember();
  }, [id, navigate]);

  const branchOptions = useMemo(() => {
    return branches ? branches.map(b => ({ label: b.name, value: b.id })) : [];
  }, [branches]);

  const update = useCallback((key, val) => setForm((p) => ({ ...p, [key]: val })), []);

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isHeic = file.name?.toLowerCase().endsWith(".heic") || file.name?.toLowerCase().endsWith(".heif") || file.type === "image/heic" || file.type === "image/heif";
    if (!isHeic) {
      setPhotoPreview(URL.createObjectURL(file));
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("photo", res.data.url);
      setPhotoPreview(`${BACKEND_URL}${res.data.url}`);
      toast.success("Photo uploaded");
    } catch {
      toast.error("Photo upload failed");
      if (!isHeic) setPhotoPreview(null);
    }
  };

  const removePhoto = () => { setPhotoPreview(null); update("photo", ""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Name and phone are required"); return; }
    if (!dobDay || !dobMonth || !dobYear) { toast.error("Date of birth is required"); return; }
    if (branches && branches.length > 0 && (!form.branchIds || form.branchIds.length === 0)) {
      toast.error("At least one branch must be selected");
      return;
    }
    const dob = `${dobYear}-${dobMonth}-${dobDay}`;
    if (new Date(dob) > new Date()) { toast.error("Date of birth cannot be in the future"); return; }

    setLoading(true);
    try {
      const payload = {
        ...form,
        dob,
        height: toNumOrNull(form.height),
        weight: toNumOrNull(form.weight),
        // Only send password if filled — backend ignores empty string
        password: newPassword.trim(),
      };
      await api.put(`/members/${id}`, payload);
      toast.success("Member updated successfully!");
      navigate(`/members/${id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update member"); }
    finally { setLoading(false); }
  };

  if (fetchLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="member-edit-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/members/${id}`)} className="rounded-sm">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold font-heading tracking-tight">Edit Member</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Personal Info ── */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="p-3 md:px-6">
              <CardTitle className="text-lg font-heading">Personal Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-3 md:px-6">

              {/* Photo */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative w-20 h-20 rounded-sm bg-secondary flex items-center justify-center overflow-hidden border border-border">
                  {photoPreview
                    ? <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                    : <Upload className="h-6 w-6 text-muted-foreground" />}
                  {photoPreview && (
                    <button type="button" onClick={removePhoto}
                      className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="photo" className="cursor-pointer text-primary text-sm hover:underline">
                    {form.photo ? "Change Photo" : "Upload Photo"}
                  </Label>
                  <p className="text-xs text-muted-foreground">JPG / PNG</p>
                  <input id="photo" type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={form.name} onChange={(e) => update("name", e.target.value)} required className="rounded-sm mt-1" data-testid="edit-name" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <MemoizedPhoneInput
                    defaultCountry="in"
                    value={form.phone}
                    onChange={(v) => update("phone", v || "")}
                    inputClassName="flex h-10 w-full rounded-sm border border-input bg-secondary px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-primary/30"
                    className="mt-1"
                    data-testid="edit-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label>Gender</Label>
                  <GenderSelect value={form.gender} onChange={(v) => update("gender", v)} />
                </div>
              </div>

              {/* DOB — separate Day / Month / Year selectors, required */}
              <div>
                <Label>Date of Birth *</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <DobSelect value={dobDay} onChange={setDobDay} placeholder="Day" options={DAY_OPTIONS} testId="dob-day" />
                  <DobSelect value={dobMonth} onChange={setDobMonth} placeholder="Month" options={MONTH_OPTIONS} testId="dob-month" />
                  <DobSelect value={dobYear} onChange={setDobYear} placeholder="Year" options={YEAR_OPTIONS} testId="dob-year" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Joining Date</Label>
                  <Input type="date" value={form.joiningDate} onChange={(e) => update("joiningDate", e.target.value)} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label>Status</Label>
                  <StatusSelect value={form.status} onChange={(v) => update("status", v)} />
                </div>
              </div>

              <div>
                <Label>Biometric / Device User ID</Label>
                <Input
                  value={form.biometricId || ""}
                  onChange={(e) => update("biometricId", e.target.value)}
                  placeholder="e.g. 101 or M101"
                  className="rounded-sm mt-1 font-mono"
                  data-testid="edit-biometric-id"
                />
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  User ID configured on the biometric machine
                </p>
              </div>

              {branches && branches.length > 0 && (
                <div>
                  <Label className="flex items-center gap-1.5 mb-1.5">Branches *</Label>
                  <MemoizedMultiSelect
                    options={branchOptions}
                    selected={form.branchIds || []}
                    onChange={(vals) => {
                      update("branchIds", vals);
                      update("branchId", vals[0] || "");
                    }}
                    placeholder="Select branches..."
                  />
                </div>
              )}

              {/* Password change (optional) */}
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Leave blank to keep current password"
                  className="rounded-sm mt-1"
                  data-testid="edit-password"
                />
              </div>

            </CardContent>
          </Card>

          {/* ── Address ── */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="p-3 md:px-6">
              <CardTitle className="text-lg font-heading">Address</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-3 md:px-6">
              <div>
                <Label>Address</Label>
                <Textarea value={form.address} onChange={(e) => update("address", e.target.value)} rows={2} className="rounded-sm mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{getStateLabel(form.country)}</Label>
                  {getStatesForCountry(form.country).length > 0 ? (
                    <StateSelect value={form.state} onChange={(v) => update("state", v)} country={form.country} />
                  ) : (
                    <Input value={form.state} onChange={(e) => update("state", e.target.value)} className="rounded-sm mt-1" placeholder={`Enter ${getStateLabel(form.country)}`} />
                  )}
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={form.city} onChange={(e) => update("city", e.target.value)} className="rounded-sm mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pincode</Label>
                  <Input type="number" inputMode="numeric" value={form.pincode} onChange={(e) => update("pincode", e.target.value)} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label>Country</Label>
                  <CountrySelect value={form.country} onChange={(v) => { update("country", v); update("state", ""); }} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Emergency Contact ── */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="p-3 md:px-6">
              <CardTitle className="text-lg font-heading">Emergency Contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-3 md:px-6">
              <div>
                <Label>Contact Name</Label>
                <Input value={form.emergencyContactName} onChange={(e) => update("emergencyContactName", e.target.value)} className="rounded-sm mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Relation</Label>
                  <RelationSelect value={form.emergencyContactRelation} onChange={(v) => update("emergencyContactRelation", v)} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <MemoizedPhoneInput
                    defaultCountry="in"
                    value={form.emergencyContactPhone}
                    onChange={(v) => update("emergencyContactPhone", v || "")}
                    inputClassName="flex h-10 w-full rounded-sm border border-input bg-secondary px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-primary/30"
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Fitness & Health ── */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="p-3 md:px-6">
              <CardTitle className="text-lg font-heading">Fitness & Health</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-3 md:px-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Height (cm)</Label>
                  <Input
                    type="number" inputMode="decimal" min={0} step="0.1"
                    value={form.height}
                    onChange={(e) => update("height", e.target.value)}
                    placeholder="Optional" className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label>Weight (kg)</Label>
                  <Input
                    type="number" inputMode="decimal" min={0} step="0.1"
                    value={form.weight}
                    onChange={(e) => update("weight", e.target.value)}
                    placeholder="Optional" className="rounded-sm mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Medical Issue</Label>
                <Textarea value={form.medicalIssue} onChange={(e) => update("medicalIssue", e.target.value)} rows={2} className="rounded-sm mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Professional</Label>
                  <Input value={form.professional} onChange={(e) => update("professional", e.target.value)} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label>Reference</Label>
                  <Input value={form.reference} onChange={(e) => update("reference", e.target.value)} className="rounded-sm mt-1" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} className="rounded-sm mt-1" />
              </div>
            </CardContent>
          </Card>

        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" type="button" onClick={() => navigate(`/members/${id}`)} className="rounded-sm" data-testid="cancel-btn">
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="rounded-sm min-w-[130px]" data-testid="save-member-btn">
            <Save className="h-4 w-4 mr-2" />
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}
